import { useState, useCallback, useMemo } from 'react';
import { Box, Typography, Button, Grid, Alert, Chip, Snackbar, Paper, TextField, MenuItem, Select, FormControl, InputLabel, Tabs, Tab } from '@mui/material';
import { Add, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useWorkflowList, WorkflowCard } from '@/components/workflow';
import type { WorkflowInstance } from '@/components/workflow';
import { useDataProvider } from 'react-admin';

const DISCIPLINARY_EARLY_STATES = ['pending'];
const PIP_EARLY_STATES = ['draft', 'active'];

const STATUS_OPTIONS = [
  { id: 'active', label: 'Active' },
  { id: 'paused', label: 'Paused' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
];

type WorkflowTypeFilter = 'all' | 'disciplinarywarning' | 'pip';

export const IndustrialRelationsWorkflowManager = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();

  const [filters, setFilters] = useState<{
    status?: string[];
    search?: string;
  }>({});

  const [workflowTypeTab, setWorkflowTypeTab] = useState<WorkflowTypeFilter>('all');

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning';
  }>({ open: false, message: '', severity: 'success' });

  const workflowTypes = useMemo(() => {
    if (workflowTypeTab === 'all') {
      return ['disciplinarywarning', 'pip'];
    }
    return [workflowTypeTab];
  }, [workflowTypeTab]);

  const { workflows, total, isLoading, refetch } = useWorkflowList({
    dataProvider,
    filters: { ...filters, workflowType: workflowTypes },
    perPage: 100,
  });

  const stats = useMemo(() => {
    const active = workflows.filter(w => w.status === 'active').length;
    const paused = workflows.filter(w => w.status === 'paused').length;
    const completed = workflows.filter(w => w.status === 'completed' || w.status === 'cancelled').length;
    return { active, paused, completed };
  }, [workflows]);

  const disciplinaryCount = useMemo(() => {
    return workflows.filter(w => w.workflowType === 'disciplinarywarning').length;
  }, [workflows]);

  const pipCount = useMemo(() => {
    return workflows.filter(w => w.workflowType === 'pip').length;
  }, [workflows]);

  const handleFilterChange = useCallback((field: 'status' | 'search', value: string | string[] | undefined) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleTabChange = useCallback((_: unknown, newValue: WorkflowTypeFilter) => {
    setWorkflowTypeTab(newValue);
  }, []);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleContinue = useCallback((workflow: WorkflowInstance) => {
    if (workflow.workflowType === 'disciplinarywarning') {
      navigate('/wizard/disciplinary');
    } else if (workflow.workflowType === 'pip') {
      navigate('/wizard/pip');
    }
  }, [navigate]);

  const handleAdvance = useCallback(async (workflowId: string, event: string) => {
    const workflow = workflows.find(w => w.id === workflowId);
    if (!workflow) return;

    try {
      const ruleResponse = await fetch('/api/v1/workflow-rules/evaluate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ruleId: `${workflow.workflowType}-guards`,
          context: {
            entityId: workflow.entityId,
            entityType: workflow.workflowType,
            currentState: workflow.currentState.value,
            event,
          },
        }),
      });

      if (ruleResponse.ok) {
        const ruleResult = await ruleResponse.json();
        if (!ruleResult.allowed) {
          setSnackbar({
            open: true,
            message: `Guard blocked: ${ruleResult.reason || 'Rule-based restriction'}`,
            severity: 'warning',
          });
          return;
        }
      }
    } catch {
      // Rule evaluation failure — proceed with transition
    }

    try {
      const response = await fetch(
        `/api/v1/workflow-instances/${workflowId}/transition`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ event }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Transition failed');
      }

      setSnackbar({ open: true, message: 'Workflow advanced successfully', severity: 'success' });
      refetch();
    } catch {
      setSnackbar({ open: true, message: 'Failed to advance workflow', severity: 'error' });
    }
  }, [workflows, refetch]);

  const handlePause = useCallback(async (workflowId: string) => {
    try {
      const workflow = workflows.find(w => w.id === workflowId);
      if (!workflow || !dataProvider) return;

      await dataProvider.update('workflow-instances', {
        id: workflowId,
        data: { ...workflow, status: 'paused', pausedAt: new Date().toISOString() },
        previousData: {},
      });
      setSnackbar({ open: true, message: 'Workflow paused', severity: 'success' });
      refetch();
    } catch {
      setSnackbar({ open: true, message: 'Failed to pause workflow', severity: 'error' });
    }
  }, [workflows, dataProvider, refetch]);

  const handleResume = useCallback(async (workflowId: string) => {
    try {
      const workflow = workflows.find(w => w.id === workflowId);
      if (!workflow || !dataProvider) return;

      await dataProvider.update('workflow-instances', {
        id: workflowId,
        data: { ...workflow, status: 'active', pausedAt: null },
        previousData: {},
      });
      setSnackbar({ open: true, message: 'Workflow resumed', severity: 'success' });
      refetch();
    } catch {
      setSnackbar({ open: true, message: 'Failed to resume workflow', severity: 'error' });
    }
  }, [workflows, dataProvider, refetch]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Industrial Relations Workflows
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage disciplinary cases and PIPs
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={isLoading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/wizard/disciplinary')}
            sx={{ mr: 1 }}
          >
            New Disciplinary Case
          </Button>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => navigate('/wizard/pip')}
          >
            New PIP
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Stats:
          </Typography>
          <Chip
            label={`${stats.active} Active`}
            color="primary"
            size="small"
            sx={{ fontWeight: 600 }}
          />
          <Typography variant="body2" color="text.secondary">|</Typography>
          <Chip
            label={`${stats.paused} Paused`}
            color="warning"
            size="small"
            sx={{ fontWeight: 600 }}
          />
          <Typography variant="body2" color="text.secondary">|</Typography>
          <Chip
            label={`${stats.completed} Completed`}
            color="success"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Box>
      </Paper>

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={workflowTypeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="All" value="all" />
          <Tab
            label={`Disciplinary (${disciplinaryCount})`}
            value="disciplinarywarning"
          />
          <Tab
            label={`PIP (${pipCount})`}
            value="pip"
          />
        </Tabs>

        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Search"
                placeholder="Search by name or reference"
                fullWidth
                size="small"
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={filters.status?.[0] || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value ? [e.target.value] : undefined)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {STATUS_OPTIONS.map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {isLoading ? (
        <Typography sx={{ py: 4, textAlign: 'center' }}>Loading workflows...</Typography>
      ) : workflows.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            No industrial relations workflows found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {filters.status?.length || filters.search
              ? 'No workflows match your filters. Try adjusting your search criteria.'
              : 'Get started by creating a new disciplinary case or PIP.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/wizard/disciplinary')}
            >
              New Disciplinary Case
            </Button>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => navigate('/wizard/pip')}
            >
              New PIP
            </Button>
          </Box>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {workflows.map((workflow) => (
            <Grid key={workflow.id} size={{ xs: 12, md: 6, lg: 4 }}>
              <WorkflowCard
                workflow={workflow}
                onOpen={handleContinue}
                onPause={handlePause}
                onResume={handleResume}
                onAdvance={handleAdvance}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};